import { window } from "global";
import { useEffect, useMemo } from "react";
import { MediaQuery } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { TierListHeader } from "@ugg/shared/components/TierList/TierListHeader";
import { TierListPageNav } from "@ugg/shared/components/TierList/TierListPageNav";
import { TierListFilterManager } from "@ugg/shared/components/TierList/TierListFilterManager";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { QueueTypeC, QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import {
  AugmentManifest,
  AugmentTierList,
  getAramMayhemAugmentIconUrl,
  useAramMayhemChampionTierList,
  useAramMayhemAugmentManifest,
  useAramMayhemAugmentByRarityTierList,
  useAramMayhemPerChampionAugmentTierList,
} from "@ugg/shared/hooks/aram-mayhem";

import { ALL_TIERS, TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { TierGroupCard } from "@ugg/shared/components/TierList/common/TierGroupCard";
import { useValidatedTierListParams } from "@ugg/shared/components/TierList/common/use-validated-tier-list-params";
import { AD_RAIL_MIN_CONTENT_HEIGHT } from "@ugg/shared/components/TierList/common/min-content-height";
import { asArray, asText } from "@ugg/shared/utils/safe-coerce";
import { RARITY_DISPLAY, RARITY_ORDER, filterAugmentTiers, resolveRarityParam, useAugmentRarityIndex } from "./augment-filters";

type TierListContainerProps = {
  patchNotesCTA?: React.ReactNode;
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemChampionTierListTableProps {
  // Partial: an absent tier is a legitimate payload shape, so the table
  // guards each one before mapping (see AramMayhemChampionTierList).
  tierlist: Partial<Record<TierNameValue, number[]>>;
}

export function AramMayhemChampionTierListTable(props: AramMayhemChampionTierListTableProps) {
  const { tierlist } = props || {};
  const convertedQueueType = QueueTypeC.convertToString(QueueTypeN.ARAM_MAYHEM) as string;

  return (
    <div className="flex flex-col gap-6">
      {ALL_TIERS.map((tier) => {
        // asArray: the feed is pipeline-produced and its types are an
        // assertion about a remote file, so a wrong-typed tier degrades to
        // empty rows instead of throwing on .map during render.
        const champions = asArray(tierlist[tier]);
        if (champions.length === 0) return null;
        return (
          <TierGroupCard key={tier} tier={tier}>
            {champions.map((championId) => (
              <ChampionLink
                key={championId}
                championId={championId}
                queueType={convertedQueueType}
                dim={36}
                className="[&_.champion-name]:!text-sm [&_.champion-name]:!ml-0 [&>div]:!px-0 !gap-[8px]"
              />
            ))}
          </TierGroupCard>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Augment tier rows — private inner helper shared by both augment
// tables. Renders one set of tier rows from a {tier: [augment_id]}
// map. Lifted out so AramMayhemAugmentTierListTable (one set of rows,
// flat) and AramMayhemAugmentByRarityTierListTable (one set per
// rarity, three sections) don't duplicate the same render logic.
//
// Layout comes from the shared TierGroupCard, so the augment and
// champion sections stay visually consistent when stacked on the same
// page. Cells render the real icon + manifest name; the dashed-square
// "#id" tile is the FALLBACK for an augment missing from the manifest.
// ─────────────────────────────────────────────────────────────────────

interface AugmentTierRowsProps {
  tierlist: Partial<Record<TierNameValue, number[]>>;
  manifest: AugmentManifest;
  patch: string;
}

function AugmentTierRows(props: AugmentTierRowsProps) {
  const { tierlist, manifest, patch } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {ALL_TIERS.map((tier) => {
        const augmentIds = asArray(tierlist[tier]);
        if (augmentIds.length === 0) return null;
        return (
          <TierGroupCard key={tier} tier={tier}>
            {augmentIds.map((augmentId) => {
              const entry = manifest[String(augmentId)];
              if (!entry) {
                return (
                  <div key={augmentId} className="flex items-center gap-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-dashed border-[#1c2030] bg-[#1a1d2e] font-mono text-[10px] text-[#64748b]">
                      ?
                    </div>
                    <span className="font-mono text-sm text-[#cddcea]">#{augmentId}</span>
                  </div>
                );
              }
              return (
                <div key={augmentId} className="flex items-center gap-2">
                  <img
                    src={getAramMayhemAugmentIconUrl(patch, augmentId)}
                    alt={manifest[augmentId]}
                    loading="lazy"
                    className="h-9 w-9 shrink-0 rounded-[3px]"
                  />
                  <span className="font-[Barlow] text-sm font-semibold text-white">{manifest[augmentId]}</span>
                </div>
              );
            })}
          </TierGroupCard>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Aggregated augment table — one global tier list across all
// rarities. Thin wrapper around AugmentTierRows; the wrapping exists
// for API symmetry with the champion table (both are exported as
// "...TierListTable") and for a clear extension point if the
// aggregated view eventually grows page-level chrome (filters,
// sort toggles, etc.).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentTierListTableProps {
  tierlist: Partial<Record<TierNameValue, number[]>>;
  manifest: AugmentManifest;
  patch: string;
}

export function AramMayhemAugmentTierListTable(props: AramMayhemAugmentTierListTableProps) {
  const { tierlist, manifest, patch } = props || {};
  return <AugmentTierRows tierlist={tierlist} manifest={manifest} patch={patch} />;
}

// ─────────────────────────────────────────────────────────────────────
// By-rarity augment table — three sections (Prismatic / Gold /
// Silver), each containing an independent tier grouping. Iterates
// in the canonical best-to-worst order from RARITY_ORDER. A section
// is hidden entirely if its `tiers` payload is missing (defensive
// — the JSON always carries all three rarities, but a partial
// fetch or future schema variation shouldn't crash the page).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentByRarityTierListTableProps {
  rarities: AugmentTierList["rarities"];
  manifest: AugmentManifest;
  patch: string;
}

export function AramMayhemAugmentByRarityTierListTable(props: AramMayhemAugmentByRarityTierListTableProps) {
  const { rarities, manifest, patch } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {RARITY_ORDER.map((rarity) => {
        const rarityTiers = rarities?.[rarity]?.tiers;
        if (!rarityTiers) return null;
        return (
          <section key={rarity} className="flex flex-col gap-2">
            <h3 className="text-lg font-bold text-[#e2e8f0]">{RARITY_DISPLAY[rarity]}</h3>
            <AugmentTierRows tierlist={rarityTiers} manifest={manifest} patch={patch} />
          </section>
        );
      })}
    </div>
  );
}

export const AramMayhemChampionTierListContainer = ({ patchNotesCTA }: TierListContainerProps) => {
  const { queryParams, validated } = useValidatedTierListParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST);

  const { data, loading, error } = useAramMayhemChampionTierList({ params: queryParams, ssr: true });
  const { tierlist, lastUpdated } = data || {};

  // Champion name search, mirroring the Classic tier list's. Driven by the
  // `search` query param (rendered by TierListFilterManager), so a filtered
  // view is shareable and survives reload. Names come from riot-assets — the
  // same source ChampionLink renders from — rather than a custom manifest,
  // since these are standard champion ids, not Classic's 60xxx namespace.
  const { getChampionName } = getRiotAssetsContext();
  const search = asText(validated.search);
  const searchedTierlist = useMemo(() => {
    const base = tierlist || {};
    const query = search.trim().toLowerCase();
    if (!query) return base;
    const result: Partial<Record<TierNameValue, number[]>> = {};
    for (const tier of ALL_TIERS) {
      result[tier] = asArray(base[tier]).filter((championId) => {
        const name = getChampionName(championId);
        return typeof name === "string" && name.toLowerCase().includes(query);
      });
    }
    return result;
  }, [tierlist, search, getChampionName]);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Server-rendered for SEO, mirroring the Classic tier list: the hook is
  // prefetched with `ssr: true`, so the server and the client's first render
  // read the same JSONFetcher cache entry and hydration matches.
  // `loading="lazy"` imgs inside are valid in SSR markup.
  let table;
  if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = <AramMayhemChampionTierListTable tierlist={searchedTierlist} />;
  }

  return (
    // Keeps the sticky ad rails from drifting up when the list filters down —
    // see AD_RAIL_MIN_CONTENT_HEIGHT.
    <div className={AD_RAIL_MIN_CONTENT_HEIGHT}>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      <TierListFilterManager className="my-[24px]" />
      <>{table}</>
    </div>
  );
};

//--------------------------------------------------------------------------------

export const AramMayhemAugmentTierListByRarityContainer = ({ patchNotesCTA }: TierListContainerProps) => {
  // `patch` is the VALIDATED value (latest when the URL omits
  // `?patch=`), which the manifest hook needs since it takes an
  // explicit patch rather than resolving one itself.
  const { queryParams, validated, patch } = useValidatedTierListParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_AUGMENT_TIER_LIST);

  const {
    data: augmentData,
    loading: augmentLoading,
    error: augmentError,
  } = useAramMayhemAugmentByRarityTierList({ params: queryParams, ssr: true });
  const { rarities: augmentRarities, lastUpdated } = augmentData || {};

  // Augment manifest — URL-keyed cache, so the per-champion container
  // and the SEO block on other routes share this fetch when the patch
  // matches. No additional network traffic attributable to this render.
  const {
    data: manifestData,
    loading: manifestLoading,
    error: manifestError,
  } = useAramMayhemAugmentManifest({ patch, ssr: true });
  const manifest = manifestData || ({} as AugmentManifest);

  // Rarity + name filters, both from the URL (rendered by
  // TierListFilterManager). This page already renders one section per rarity,
  // so the rarity filter hides whole sections rather than individual ids —
  // no id → rarity lookup needed here, unlike the per-champion module below.
  const rarity = resolveRarityParam(validated.rarity);
  const search = asText(validated.search);
  const visibleRarities = useMemo(() => {
    const source = augmentRarities || ({} as AugmentTierList["rarities"]);
    const result = {} as AugmentTierList["rarities"];
    for (const key of RARITY_ORDER) {
      if (rarity && key !== rarity) continue;
      const tiers = source[key]?.tiers;
      if (!tiers) continue;
      const filtered = filterAugmentTiers(tiers, { manifest, query: search });
      // Drop a rarity whose every tier emptied out, so a search that only hits
      // Prismatic doesn't leave bare "Gold"/"Silver" headers behind.
      if (!ALL_TIERS.some((tier) => asArray(filtered[tier]).length > 0)) continue;
      result[key] = { tiers: filtered };
    }
    return result;
  }, [augmentRarities, manifest, rarity, search]);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Same loading/error/render pattern as the aggregated container.
  // Gate on BOTH the by-rarity data AND the manifest so the table
  // renders once with real content — no placeholder-to-real
  // flash mid-load, which is particularly jarring with the
  // three-section by-rarity layout since sections would pop in
  // one at a time as manifest lookups resolve.
  // Server-rendered, same as the champion container above. Gated on BOTH
  // fetches so the table renders once with real content rather than
  // flashing placeholders while the manifest resolves — especially
  // visible here, where the three rarity sections would pop in one by one.
  let augmentTable;
  if (augmentLoading || manifestLoading) {
    augmentTable = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    augmentTable = <TierListTableError />;
  } else {
    augmentTable = <AramMayhemAugmentByRarityTierListTable rarities={visibleRarities} manifest={manifest} patch={patch || ""} />;
  }

  return (
    // Keeps the sticky ad rails from drifting up when the list filters down —
    // see AD_RAIL_MIN_CONTENT_HEIGHT.
    <div className={AD_RAIL_MIN_CONTENT_HEIGHT}>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={augmentLoading || manifestLoading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      <TierListFilterManager className="my-[24px]" />
      <>{augmentTable}</>
    </div>
  );
};

//--------------------------------------------------------------------------------

// Per-champion augment tier list, scoped to ONE champion's observations
// of augments. Renders inside the champion-build page rather than a
// tier-list route, so it differs from the containers above in two ways:
// the caller supplies `championKey` and the resolved `patch` (there's no
// tier-list page id to validate against here), and the page chrome
// (TierListHeader, page nav, filter manager, patch-notes CTA) is omitted
// because the build page provides its own surrounding layout.
//
// Reuses AramMayhemAugmentTierListTable from the global augment
// container — same payload shape, just a different fetch source.
interface AramMayhemPerChampionAugmentTierListContainerProps {
  championKey: number;
  patch: string | undefined;
  // Both come from the champion page's own filter bar (rendered by
  // ChampionProfileFilterManager), validated against the page's valid-params
  // by the caller. `rarity` is the raw query value; resolveRarityParam maps it.
  rarity?: unknown;
  search?: unknown;
}

export const AramMayhemPerChampionAugmentTierListContainer = ({
  championKey,
  patch,
  rarity: rarityParam,
  search: searchParam,
}: AramMayhemPerChampionAugmentTierListContainerProps) => {
  const {
    data: augmentData,
    loading: augmentLoading,
    error: augmentError,
  } = useAramMayhemPerChampionAugmentTierList({ championKey, patch, ssr: true });
  const { tierlist: augmentTierlist } = augmentData || {};

  // Augment manifest — shared lookup for icons + display names. The
  // table can render without it, but the result is unreadable ("#1234"
  // labels), so gate on BOTH fetches like the global container does.
  // Same URL as the SEO block on this page, so the fetcher's URL-keyed
  // cache dedupes them.
  const {
    data: manifestData,
    loading: manifestLoading,
    error: manifestError,
  } = useAramMayhemAugmentManifest({ patch, ssr: true });
  const manifest = manifestData || ({} as AugmentManifest);

  // Same rarity + name filters as the by-rarity tier list page. This feed is
  // flat ({tier: [id]}) so, unlike that page, rarity has to be resolved per id
  // — hence the extra index fetch. Filtering degrades to name-only while the
  // index is still loading rather than blanking the table.
  const rarity = resolveRarityParam(rarityParam);
  const search = asText(searchParam);
  const { index: rarityIndex, loading: rarityIndexLoading } = useAugmentRarityIndex({ patch });
  const filteredTierlist = useMemo(
    () =>
      filterAugmentTiers(augmentTierlist || {}, {
        manifest,
        query: search,
        rarity: rarityIndexLoading ? undefined : rarity,
        rarityIndex,
      }),
    [augmentTierlist, manifest, search, rarity, rarityIndex, rarityIndexLoading],
  );

  // Server-rendered like the route containers. Both hooks above declare
  // `ssr: true` in their own right rather than leaning on AramMayhemSEO
  // (which hits the same two URLs on this page) to warm the cache — the
  // fetcher is URL-keyed so this costs no extra request, and the table
  // keeps server-rendering if that SEO block ever moves or goes away.
  let table;
  if (augmentLoading || manifestLoading) {
    table = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    table = <TierListTableError />;
  } else {
    table = <AramMayhemAugmentTierListTable tierlist={filteredTierlist} manifest={manifest} patch={patch || ""} />;
  }

  // Same rail-collapse guard as the tier-list routes: this module is the whole
  // body of the champion page's ARAM Mayhem tab, and the champion page uses the
  // identical sticky-rail flex row (RailAdsContainer).
  return <div className={AD_RAIL_MIN_CONTENT_HEIGHT}>{table}</div>;
};

//--------------------------------------------------------------------------------

const AramMayhemBuild = () => {
  const { championId, page } = useChampionProfileContext();

  // championId is the numeric champion KEY (62 for MonkeyKing, 145 for
  // Kaisa, ...) — the same identifier the pipeline uses as the filename
  // stem for the per-champion augment JSONs, so it passes through as-is.
  //
  // Patch is resolved against the CHAMPION page's valid params (not a
  // tier-list page), which is why this validates here rather than using
  // useValidatedTierListParams.
  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams(validParams);
  const { patch, rarity, search } = validateQueryParams(page, queryParams, true);

  return <AramMayhemPerChampionAugmentTierListContainer championKey={championId} patch={patch} rarity={rarity} search={search} />;
};

export default AramMayhemBuild;

//--------------------------------------------------------------------------------
